import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataGrid,
  DataGridProps,
  DropDownMenuProps,
  GridSpecialColumnFields,
  GridSelectionModel
} from '@p81-common/ui';

import {
  GRID_DETAIL_PANEL_FIELD,
  GRID_DETAIL_PANEL_ROW_ID
} from '../helpers/config';
import { useNetworkRolloutDataGrid } from '../helpers/useNetworkRolloutDataGrid';
import { useTableSettings } from '../helpers/tableSettings';

import createClasses from './styles';
import { access, hasAccess, resources } from 'utils/permissions';

interface NetRolloutDataGridProps {
  selectionModel: GridSelectionModel;
  setSelectionModel: DataGridProps['onSelectionModelChange'];
  useDataGridResultProps: ReturnType<typeof useNetworkRolloutDataGrid>;
}

const NetRolloutDataGrid = (props: NetRolloutDataGridProps) => {
  const { selectionModel, setSelectionModel, useDataGridResultProps } = props;
  const classes = createClasses();

  const { t } = useTranslation(['activity']);

  const {
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    setFilter,
    filter,
    onSortEnd,
    rowSelectionChanged,
    tableSettingsDisplay,
    changeCurrentPage,
    numOfRowsOptions,
    tableSettingsChanged,
    results,
    tableData,
    tableConfigDisplay,
    loaderTable,
    direction,
    setDirection,
    startFilter,
    setStartFilter,
    transformGridRowData
  } = useDataGridResultProps;

  const { detailPanelTableConfig } = useTableSettings();

  const onChangeNumOfRows: DropDownMenuProps['handleClick'] = useCallback(
    itemsPerPage => () => {
      handleChangeNumOfRows(itemsPerPage)(undefined as any);
      setFilter({ ...filter, itemsPerPage: Number(itemsPerPage) });
    },
    [filter, handleChangeNumOfRows, setFilter]
  );

  const handleSortable = (field: any) => {
    setDirection(direction === 1 ? -1 : 1);
    setFilter({
      ...filter,
      fieldName: field
    });
    setStartFilter(!startFilter);
  };

  const detailPanelProps: DataGridProps['detailPanelProps'] = useMemo(
    () => ({
      getRowId: entry => entry[GRID_DETAIL_PANEL_ROW_ID],
      tableConfig: detailPanelTableConfig
    }),
    [detailPanelTableConfig]
  );

  const paginationProps: DataGridProps['paginationProps'] = useMemo(
    () => ({
      page: filter.currentPage - 1,
      count: results?.totalRecords || 0,
      rowsPerPage: numOfRowsOptions.find(
        (item: { name: number; selected: boolean }) => item.selected === true
      )!.name,
      onChangePage: (e, page) => {
        setFilter({ ...filter, currentPage: page + 1 });
        changeCurrentPage(page + 1);
      },
      hideNavigation: !results?.totalRecords,
      hideRowCount: !results?.totalRecords
    }),
    [
      changeCurrentPage,
      filter,
      numOfRowsOptions,
      results?.totalRecords,
      setFilter
    ]
  );

  const tableDataDisplay = useMemo(
    () => ({
      page: results?.page || 0,
      totalPages: results?.totalPages || 0,
      totalRecords: results?.totalRecords || 0,
      data: (tableData || []).map(transformGridRowData)
    }),
    [results, tableData, transformGridRowData]
  );
  const hasNetworkRolloutWriteAccess = hasAccess([
    resources.CS_NETWORK_ROLLOUT,
    access.WRITE
  ]);

  return (
    <DataGrid
      // fix the gap between pagination to the table.
      classes={{
        container: classes.wrapDG,
        dataGridRoot: selectionModel.length ? classes.TESTCLASS : ''
      }}
      autoHeight={true}
      tableConfig={tableConfigDisplay}
      tableData={tableDataDisplay}
      lazyLoading={true}
      loading={loaderTable}
      numOfRowsOptions={numOfRowsOptions}
      rowCount={results?.totalRecords || 0}
      initialState={{
        pinnedColumns: {
          left: [
            GridSpecialColumnFields.DETAIL_PANEL_TOGGLE,
            GridSpecialColumnFields.CHECKBOX_SELECTION,
            tableConfigDisplay[0].field
          ]
        }
      }}
      paginationProps={paginationProps}
      toolbarProps={{
        title1: 'All Network Results',
        title2: `(${results?.totalRecords || 0} Networks, ${
          results?.totalGwCount || 0
        } Gateways)`,
        exportTooltipText: t('activity:cloudManagementLogs.EXPORT'),
        tableSettingsTooltipText: t(
          'activity:cloudManagementLogs.TABLE_SETTINGS'
        ),
        rowSelectionTooltipText: t(
          'activity:cloudManagementLogs.QUANTITY_OF_ITEMS'
        ),
        tableSettingsListItems: tableSettingsDisplay,
        rowSelectionChanged,
        tableSettingsChanged,
        handleTableSettingsMenuItemClick,
        handleSearchChangeCallback,
        onChangeNumOfRows,
        onSortEnd
      }}
      onSortModelChange={a => {
        handleSortable(a[0].field);
      }}
      sortingMode="server"
      paginationMode="server"
      // DETAIL PANEL
      detailPanelField={GRID_DETAIL_PANEL_FIELD}
      detailPanelProps={detailPanelProps}
      // CHECKBOX SELECTION
      checkboxSelection={hasNetworkRolloutWriteAccess}
      selectionModel={selectionModel}
      onSelectionModelChange={setSelectionModel}
      keepNonExistentRowsSelected
    />
  );
};

export default memo(NetRolloutDataGrid);
